import type { GlobalNetwork } from "@woven/contracts";
import { globalNetworkStateTone } from "../../lib/globalNetworkStatus";
import { statusTone } from "../../lib/serverStatus";
import { EmptyState } from "../../components/StatusView";
import { MetricStrip } from "../../components/ui/MetricStrip";
import { StatusIndicator } from "../../components/ui/StatusIndicator";

const ADMIN_CONTROLS = [
  {
    label: "Add instance",
    description: "Provision a new backing instance in a region.",
  },
  {
    label: "Drain instance",
    description: "Gracefully remove an instance from rotation before decommissioning it.",
  },
  {
    label: "Force refresh cost",
    description: "Re-pull billing data outside the normal cycle.",
  },
] as const;

/**
 * The "once created" view: total instances, usage, cost, activity, and
 * state for the shared public network — distinct from a single dedicated
 * server, since this is the distributed multi-tenant deployment.
 */
export function GlobalNetworkManagement({ network }: { network: GlobalNetwork }) {
  return (
    <div className="console-page">
      <div className="console-page-head">
        <h1 className="console-page-title">Global network</h1>
        <p className="console-page-sub">
          The shared public network every lite/pro user connects to by default — as opposed to a
          user's own dedicated server.
        </p>
        <StatusIndicator tone={globalNetworkStateTone(network.state)} label={network.state} />
      </div>

      <MetricStrip
        items={[
          { label: "Total instances", value: String(network.instances.length) },
          { label: "Subspaces", value: String(network.usage.totalSubspaces) },
          { label: "Subscribers", value: String(network.usage.totalSubscribers) },
          { label: "Connections", value: String(network.usage.totalConnections) },
        ]}
      />

      <section className="panel">
        <h2 className="panel-title">Cost</h2>
        <div className="metric-grid">
          <div className="metric-tile">
            <p className="metric-label">Month to date</p>
            <p className="metric-value">{formatCents(network.cost.monthToDateCents)}</p>
          </div>
          <div className="metric-tile">
            <p className="metric-label">Projected this month</p>
            <p className="metric-value">{formatCents(network.cost.projectedMonthlyCents)}</p>
          </div>
        </div>
      </section>

      <section className="panel">
        <h2 className="panel-title">Instances</h2>
        {network.instances.length === 0 ? (
          <EmptyState
            title="No instances yet"
            body="Instances will appear here once the network's backing deployments are provisioned."
          />
        ) : (
          <table className="data-table">
            <caption className="visually-hidden">Global network instances</caption>
            <thead>
              <tr>
                <th scope="col">ID</th>
                <th scope="col">Region</th>
                <th scope="col">Status</th>
                <th scope="col">Created</th>
              </tr>
            </thead>
            <tbody>
              {network.instances.map((instance) => (
                <tr key={instance.id}>
                  <td className="cell-mono">{instance.id}</td>
                  <td className="cell-mono">{instance.region}</td>
                  <td>
                    <StatusIndicator
                      tone={statusTone(instance.status)}
                      label={instance.status}
                      mono
                    />
                  </td>
                  <td className="cell-mono">{formatDate(instance.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="panel">
        <h2 className="panel-title">Activity</h2>
        {network.activity.length === 0 ? (
          <p className="page-prose">No activity yet.</p>
        ) : (
          <ul className="page-list">
            {network.activity.map((entry) => (
              <li key={entry.id}>
                {entry.message} — <span className="cell-mono">{formatDate(entry.occurredAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="panel-title">Admin controls</h2>
        <div className="admin-grid">
          {ADMIN_CONTROLS.map((control) => (
            <article className="panel admin-card" key={control.label}>
              <h3 className="feature-title">{control.label}</h3>
              <p className="page-prose">{control.description}</p>
              <button type="button" className="btn btn--ghost" disabled>
                Not Implemented
              </button>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function formatCents(cents: number): string {
  return (cents / 100).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
